import { type ReactNode } from "react";
import { notifications, type NotificationData } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconCheck,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import { DESIGN_ICONS } from "../theme/designIcons";
import {
  ReleaseNoteToast,
  releaseNoteToastClassNames,
} from "../components/notifications/ReleaseNoteToast";

/**
 * Brand-aligned notification helpers.
 *
 * Use these INSTEAD of calling `notifications.show({ color: "green" | "red" | ... })`
 * directly. Hand-picking a Mantine color name yields off-brand greens / oranges /
 * blues that make the app look inconsistent; these apply the semantic brand color
 * (`--color-success` / `-danger` / `-warning` / `-info`) plus a consistent icon so
 * every toast reads as one system. See docs/DESIGN_SYSTEM.md.
 *
 *   notifySuccess("Client saved");
 *   notifyError("Could not save client", { title: "Save failed" });
 *
 * `options` accepts anything `notifications.show` does except `message`/`color`
 * (pass `icon` to override the default icon).
 */

const ICON_SIZE = 18;

export type NotifyOptions = Omit<NotificationData, "message" | "color">;

const notify = (
  color: string,
  icon: ReactNode,
  message: ReactNode,
  options: NotifyOptions = {},
) =>
  notifications.show({
    message,
    color,
    icon,
    ...options,
  });

export const notifySuccess = (message: ReactNode, options?: NotifyOptions) =>
  notify(
    "var(--color-success)",
    <IconCheck size={ICON_SIZE} />,
    message,
    options,
  );

export const notifyError = (message: ReactNode, options?: NotifyOptions) =>
  notify("var(--color-danger)", <IconX size={ICON_SIZE} />, message, options);

export const notifyWarning = (message: ReactNode, options?: NotifyOptions) =>
  notify(
    "var(--color-warning)",
    <IconAlertTriangle size={ICON_SIZE} />,
    message,
    options,
  );

export const notifyInfo = (message: ReactNode, options?: NotifyOptions) =>
  notify(
    "var(--color-info)",
    <IconInfoCircle size={ICON_SIZE} />,
    message,
    options,
  );

export interface NotifyReleaseNoteOptions {
  releaseNoteId: string;
  title: string;
  summary: string;
  link?: string | null;
  /** Marks the note read; awaited (≤2s) by the toast before reloading. */
  onRefresh: () => Promise<void> | void;
  /** Called when the toast is hidden (e.g. the note was read elsewhere). */
  onClose?: () => void;
}

export const releaseNoteToastId = (releaseNoteId: string) =>
  `release-note-${releaseNoteId}`;

/**
 * Sticky "a new version is available" toast for a published release note, in
 * the brand teal. It can't be dismissed: there's no close button, and Refresh
 * (mark read + reload) is the only way to clear it. Keyed by release note so a repeat call
 * never stacks a second toast for the same note.
 */
export const notifyReleaseNote = ({
  releaseNoteId,
  title,
  summary,
  link,
  onRefresh,
  onClose,
}: NotifyReleaseNoteOptions) => {
  const AnnouncementIcon = DESIGN_ICONS.Megaphone;
  return notifications.show({
    id: releaseNoteToastId(releaseNoteId),
    color: "var(--brand-teal)",
    icon: <AnnouncementIcon size={ICON_SIZE} />,
    title,
    message: (
      <ReleaseNoteToast summary={summary} link={link} onRefresh={onRefresh} />
    ),
    autoClose: false,
    withCloseButton: false,
    classNames: releaseNoteToastClassNames,
    onClose,
  });
};

/** Close a release-note toast (e.g. once the note was read elsewhere). */
export const hideReleaseNoteToast = (releaseNoteId: string) =>
  notifications.hide(releaseNoteToastId(releaseNoteId));
